<template>
    <div class="main-content-container container-fluid px-4">
        <!-- Page Header -->
        <div class="page-header row no-gutters py-4">
            <div class="col-12 text-center text-sm-left mb-0 flex-header">
                <h3 class="page-title">Lista faktur</h3>
                <d-button
                    @click="exportInvoices()"
                    :class="{ isLoading : zip_downloading }">
                    <i class="material-icons">save_alt</i> Pobierz wszystkie wyniki
                </d-button>
            </div>
        </div>

        <!-- Default Light Table -->
        <template v-if="loadError">
            <d-alert
                show
                theme="warning">
                Wczytywanie danych nie powiodło się.
                <a
                    href="javascript:window.location.reload();"
                    class="alert-link">
                    Odśwież stronę
                </a>
                aby spróbować ponownie.
            </d-alert>
        </template>
        <template v-else>
            <transition name="fade">
                <d-card class="card-small">
                    <div
                        v-if="!loaded"
                        class="overlay-loading">
                        <h2 class="preloader isLoading">Trwa ładowanie</h2>
                    </div>
                    <d-card-header class="border-bottom flex-header">
                        <div class="search-wrapper">
                            <d-input-group>
                                <v-select
                                    v-model="filters.company_id"
                                    :options="allCompanies"
                                    :placeholder="'Spółka'"
                                    :reduce="(option) => option.id"
                                    class="autocomplete-wrapper"
                                    label="name" />
                                <d-input
                                    class="form-control"
                                    placeholder="Imię"
                                    v-model="filters.name" />
                                <d-input
                                    class="form-control"
                                    placeholder="Nazwisko"
                                    v-model="filters.surname" />
                                <ss-date
                                    placeholder="Data od"
                                    v-model="filters.date_from"
                                    :limitto="filters.date_to" />
                            </d-input-group>
                            <d-input-group>
                                <d-input
                                    class="form-control"
                                    placeholder="Email"
                                    v-model="filters.email" />
                                <d-input
                                    class="form-control"
                                    placeholder="Telefon"
                                    v-model="filters.phone" />
                                <d-select
                                    type="text"
                                    name="type"
                                    v-model="filters.type">
                                    <option value="0">--Wybierz typ</option>
                                    <option
                                        v-for="invoiceType in invoiceTypes"
                                        :key="invoiceType.value"
                                        :value="invoiceType.value" >
                                        {{ invoiceType.text }}
                                    </option>
                                </d-select>
                                <ss-date
                                    placeholder="Data do"
                                    v-model="filters.date_to"
                                    :limitfrom="filters.date_from" />
                                <button
                                    class="button btn autocomplete-clear btn-primary"
                                    @click.prevent="filterList">
                                    Szukaj
                                </button>
                                <button
                                    class="button btn autocomplete-clear btn-secondary"
                                    @click.prevent="clearFilters">
                                    Wyczyść
                                </button>
                            </d-input-group>
                        </div>
                    </d-card-header>
                    <d-card-body class="p-0 pb-2 text-center">
                        <table class="table mb-0 responsive-table vam">
                            <thead class="bg-light">
                                <tr>
                                    <th scope="col" class="border-0 id-cell">ID</th>
                                    <th scope="col" class="border-0">Klient</th>
                                    <th scope="col" class="border-0">Spółka</th>
                                    <th scope="col" class="border-0">Data wygenerowania</th>
                                    <th scope="col" class="border-0">Nr faktury</th>
                                    <th scope="col" class="border-0">Kwota faktury</th>
                                    <th scope="col" class="border-0">Typ</th>
                                    <th scope="col" class="border-0">Akcje</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(invoice, index) in invoices">
                                    <td class="id-cell">
                                        <dt>ID</dt>
                                        <dd>
                                            {{ invoice.id }}
                                        </dd>
                                    </td>
                                    <td>
                                        <dt>Klient</dt>
                                        <dd>
                                            {{ invoice.client_name }}
                                        </dd>
                                    </td>
                                    <td>
                                        <dt>Spółka</dt>
                                        <dd>{{ invoice.company_name }}</dd>
                                    </td>
                                    <td>
                                        <dt>Data wygenerowania</dt>
                                        <dd>{{ invoice.generate_date }}</dd>
                                    </td>
                                    <td>
                                        <dt>Nr faktury</dt>
                                        <dd>{{ invoice.invoice_number }}</dd>
                                    </td>
                                    <td>
                                        <dt>Kwota faktury</dt>
                                        <dd>{{ toNumber(invoice.price) }} zł</dd>
                                    </td>
                                    <td>
                                        <dt>Typ</dt>
                                        <dd>{{ getInvoiceTypeLabel(invoice.type) }}</dd>
                                    </td>

                                    <td>
                                        <dt>Akcje</dt>
                                        <dd>
                                            <d-button @click="openInvoice(invoice, index)" :class="{ isLoading :isLoading === index }"><i class="material-icons">save_alt</i> Pobierz</d-button>
                                            <!--<d-button theme="danger"  class="mr-1 mr-3" @click="removeInvoice(invoice.id)">Usuń</d-button>-->
                                        </dd>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <p
                            v-if="!invoices"
                            class="text-info">Brak umów do wyświetlenia.
                        </p>
                    </d-card-body>
                    <d-card-footer>
                        <ss-pagination
                            :meta-data="paginationMetaData"
                            :initial-page="currentPage" />
                    </d-card-footer>
                </d-card>
            </transition>
        </template>
    </div>
</template>

<script>
import FileSaver from 'file-saver';
import { mapGetters, mapActions } from 'vuex';
import SsDate from '@/components/common/SsDate';
import clientUtils from '@/utils/client';
import SsPagination from '@/components/common/SsPaginationNew';
import HasFilters from '@/mixins/HasFilters.vue';
import HasPagination from '@/mixins/HasPagination.vue';
import ListUtils from '@/utils/ListUtils.js';

export default {
    name: 'InvoicesList',
    mixins: [HasFilters, HasPagination],
    components: {
        SsDate,
        clientUtils,
        SsPagination
    },
    data () {
        return {
            client: '',
            filters: {
                company_id: '',
                date_from: '',
                date_to: '',
                email: '',
                name: '',
                phone: '',
                surname: '',
                type: '0'
            },
            invoices: [],
            invoiceTypes: [{
                text: 'Odsetkowa',
                value: 'interest'
            }, {
                text: 'Prowizyjna',
                value: 'provision'
            }],
            isLoading: '',
            loaded: false,
            loadError: false,
            statuses: [],
            zip_downloading: false
        }
    },
    computed: {
        ...mapGetters ([
            'allCompanies',
            'allClients'
        ])
    },
    mounted () {
        this.statuses = clientUtils._statuses;
        this.getAllClients();
        this.getAllCompanies();
        this.loadInvoices();
    },
    methods: {
        ...mapActions ([
            'getAllCompanies',
            'getAllClients'
        ]),
        filterList () {
            Vue.set(this, 'loaded', false);
            Vue.set(this, 'loadError', false);
            this.$bus.$emit('pagination-reset');
            this.$bus.$emit('view-filters-save');
        },
        clearFilters () {
            Vue.set(this, 'loaded', false);
            Vue.set(this, 'loadError', false);
            this.filters = {
                company_id: '',
                email: '',
                name: '',
                phone: '',
                surname: '',
                type: '0'
            };
            this.$bus.$emit('view-filters-reset');
            this.$bus.$emit('pagination-reset');
        },
        toNumber (number) {
            return Vue.toNumber(number);
        },
        loadInvoices () {
            ListUtils.loadItemsData(this, {
                method: 'post',
                endpoint: '/api/invoices/get-invoices',
                listField: 'invoices',
                pagination: true,
                params: {
                    page: this.currentPage,
                    where: this.filters,
                    short: true
                }
            });
        },
        debouncedLoadFilteredData: _.debounce(function () {
            Vue.set(this, 'loaded', false);
            Vue.set(this, 'loadError', false);
            this.loadInvoices();
        }, 500),
        exportInvoices () {
            this.zip_downloading = true;
            axios.post('api/invoices/download-zip',
                { page: this.current_page, where: this.filters, pagination: 0 } ,
                { responseType: 'blob' }).then(response => {
                let blob = new File(
                    [response.data],
                    'lista_faktur_' + Vue.getCurrentDate() + '_.zip',
                    { type: 'application/zip' }
                );
                FileSaver.saveAs(blob);
                this.zip_downloading = false;
                // this['exporting_' + type] = false;
            }).catch( error =>{
                this.zip_downloading = false;
                // this['exporting_' + type] = false;
            })
        },
        exportInvoiceToPdf (invoice, index) {
            // this['exporting_' + type] = true;
            this.isLoading = index;
            axios.post('api/invoices/download-invoice',
                {
                    invoice_id: invoice.id
                },
                {responseType: 'blob'}).then(response => {
                let blob = new File(
                    [response.data],
                    invoice.client_name.replace(' ', '_') + '_faktura_' + (invoice.type === 'interest' ? 'odsetkowa' : 'prowizyjna') + '_' +  invoice.invoice_number + '_' + invoice.generate_date + '.pdf',
                    { type: 'application/pdf' }
                );
                FileSaver.saveAs(blob);
                this.isLoading = '';
                // this['exporting_' + type] = false;
            }).catch( error =>{
                this.isLoading = '';
                // this['exporting_' + type] = false;
            })
        },
        openInvoice (invoice, index) {
            if (invoice.id <= 39) {
                this. exportInvoiceToPdf(invoice, index)
            } else {
                window.open(invoice.view_url, '_blank')
            }
        },
        getInvoiceTypeLabel (invoiceType) {
            let invoiceTypeFound = this.invoiceTypes.find(item => item.value === invoiceType);

            if (!invoiceTypeFound) {
                return 'brak';
            }

            return invoiceTypeFound.text;
        }
    }
}
</script>

<style lang="scss">
    #select-overdue{
        width: 200px;
    }
</style>
